<script setup>
import { onMounted, onUnmounted, defineProps, ref, computed, watch, nextTick } from 'vue';
import showMoreConfigDefaults from '../show-more/show-more.config';
import { ShowMoreRevealModel } from '../show-more/ShowMoreRevealModel';
import { ViewportBreakpointBroadcaster } from '../show-more/ViewportBreakpointBroadcaster';
import { findEventsOnDateInList } from '../show-more/FindEventsOnDateInList';
import ShowMoreButton from '../show-more/ShowMoreButton.vue';

const props = defineProps({
  events: {
    type: Array,
    default: () => []
  },
  year: {
    type: Number,
    default: undefined
  },
  type: {
    type: String,
    default: undefined
  },
  // Опциональные переопределения для app/show-more/show-more.config.js
  showMore: {
    type: Object,
    default: null
  }
})

const selectedDate = ref(undefined);
const targetYear = computed(() => parseInt(props.year));
const selectedMonth = ref(null);
const activeEventId = ref(null);
const selectedEvent = ref(undefined);

// Функция для создания уникального ID события
const getEventId = (event) => {
  const finishPart = event.date_finish ? `_${event.date_finish}` : '';
  const linkPart = event.link ? `_${event.link}` : '';
  const cityPart = event.city ? `_${event.city}` : '';
  return `${event.name}_${event.date_start}${finishPart}${linkPart}${cityPart}`;
};

const startDate = computed(() => {
  if (targetYear.value !== new Date().getFullYear()) {
    return new Date(targetYear.value, 0, 1);
  }
  return undefined;
});

// Функция фильтрации событий для календаря (по году и типу)
const filterEventsForCalendar = (events, type, year) => {
  let result = events || [];

  // Фильтрация по году
  if (year) {
    result = result.filter(event => {
      const eventYear = new Date(event.date_start).getFullYear();
      if (event.date_finish) {
        const finishYear = new Date(event.date_finish).getFullYear();
        return eventYear === year || finishYear === year;
      }
      return eventYear === year;
    });
  }

  // Фильтрация по типу
  if (type) {
    result = result.filter(event => event.type === type);
  }

  return result;
};

// Всегда показываем полный год в календаре
const dateRange = computed(() => {
  const year = targetYear.value || new Date().getFullYear();
  return {
    minDate: new Date(year, 0, 1),
    maxDate: new Date(year, 11, 31)
  };
});

const minDate = computed(() => dateRange.value.minDate);
const maxDate = computed(() => dateRange.value.maxDate);

const eventTypeName = {online: 'Онлайн', offline: 'Офлайн'}

const formatDateRange = (date1, date2 = null, whitespace = true) => {
  const divider = whitespace ? ' ' : '';
  const getMonthName = (month) => {
    const monthNames = [
      'января', 'февраля', 'марта', 'апреля', 'мая', 'июня',
      'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря'
    ];
    return monthNames[month - 1];
  }

  const [year1, month1, day1] = date1.split('-').map(Number);

  if (!date2) {
    return `<h3>${day1}</h3><span>${getMonthName(month1)}</span>`;
  }

  const [year2, month2, day2] = date2.split('-').map(Number);

  if (year1 === year2 && month1 === month2) {
    return `<h3>${day1}${divider}-${divider}${day2}</h3><span>${getMonthName(month1)}</span>`;
  }

  return `<h3>${day1} <span>${getMonthName(month1)}</span> - ${day2} <span>${getMonthName(month2)}</span></h3>`;
};

/** Фраза «на июнь 2026 г.» (например) с неразрывными пробелами после «на» и перед «г.» */
const formatEmptyListMonthPhrase = (monthSelection) => {
  if (!monthSelection) {
    return '';
  }
  const monthName = new Date(monthSelection.year, monthSelection.month - 1).toLocaleDateString('ru-RU', {
    month: 'long',
  });
  return `на\u00A0${monthName} ${monthSelection.year}\u00A0г.`;
};

const getDatesBetween = (start, end) => {
  const dates = [];
  let current = new Date(start);
  const last = new Date(end);

  current.setHours(0, 0, 0, 0);
  last.setHours(0, 0, 0, 0);

  while (current <= last) {
    dates.push(new Date(current));
    current.setDate(current.getDate() + 1);
  }
  return dates;
}

const parseDate = (isoDate) => {
  const [year, month, day] = isoDate.split('-').map(Number);
  return new Date(year, month - 1, day);
}

const highlightedDates = ref([]);

// Инициализация highlightedDates для календаря с учетом фильтра по году и типу
const initializeHighlightedDates = () => {
  const filteredEvents = filterEventsForCalendar(props.events, props.type, targetYear.value);
  highlightedDates.value = filteredEvents?.flatMap(event => {
    if (event.date_finish) {
      return getDatesBetween(parseDate(event.date_start), parseDate(event.date_finish));
    }
    return [parseDate(event.date_start)];
  });
};

// Функция для проверки, попадает ли мероприятие в выбранный месяц
const isEventInSelectedMonth = (event) => {
  if (!selectedMonth.value) return true;

  const eventStart = parseDate(event.date_start);
  const eventEnd = event.date_finish ? parseDate(event.date_finish) : eventStart;

  const selectedMonthStart = new Date(selectedMonth.value.year, selectedMonth.value.month - 1, 1);
  const selectedMonthEnd = new Date(selectedMonth.value.year, selectedMonth.value.month, 0, 23, 59, 59);

  return (eventStart <= selectedMonthEnd && eventEnd >= selectedMonthStart);
};

const handleDateClick = (date) => {
  const normalizedDate = new Date(date);
  normalizedDate.setHours(0, 0, 0, 0);

  const {
    matchingEvents,
    lastIndex,
  } = findEventsOnDateInList(filteredEventsForList.value, normalizedDate, parseDate);

  if (matchingEvents.length > 0) {
    if (resolvedShowMoreConfig.value.revealOnCalendarDateSelect) {
      showMoreRevealModel.ensureVisibleThroughIndex(lastIndex);
      syncShowMoreViewState();
    }

    const firstEvent = matchingEvents[0];
    activeEventId.value = getEventId(firstEvent);
    selectedEvent.value = firstEvent;

    nextTick(() => {
      const activeElement = document.querySelector('.events-list__item_active');
      if (activeElement) {
        activeElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    });
  } else {
    activeEventId.value = null;
    selectedEvent.value = undefined;
    alert('На выбранную дату нет событий');
  }
  selectedDate.value = date;
};

// Обработчик изменения месяца в календаре
const handleMonthChange = (event) => {
  selectedMonth.value = {
    year: event.year,
    month: event.month + 1
  };
  activeEventId.value = null;
  selectedEvent.value = undefined;
  selectedDate.value = undefined;
};

// Функция фильтрации по году (только для списка мероприятий)
const filterEventsByYear = (events, year) => {
  return events?.filter(event => {
    const eventYear = new Date(event.date_start).getFullYear();
    if (event.date_finish) {
      const finishYear = new Date(event.date_finish).getFullYear();
      return eventYear === year || finishYear === year;
    }
    return eventYear === year;
  });
};

// Функция фильтрации событий по типу
const filterEventsByType = (events, type) => {
  if (!type) return events;
  return events?.filter(event => event.type === type);
};

// Отфильтрованные события для списка (с фильтрацией по году, типу и месяцу)
const filteredEventsForList = computed(() => {
  let result = props.events;

  // Фильтрация по году
  result = filterEventsByYear(result, targetYear.value);

  // Фильтрация по типу
  result = filterEventsByType(result, props.type);

  // Фильтрация по выбранному месяцу
  if (selectedMonth.value) {
    result = result.filter(event => isEventInSelectedMonth(event));
  }

  return result;
});

const resolvedShowMoreConfig = computed(() => ({
  ...showMoreConfigDefaults,
  ...(props.showMore || {}),
}));

const showMoreRevealModel = new ShowMoreRevealModel(resolvedShowMoreConfig.value);
const showMoreCurrentPageSize = ref(
  showMoreRevealModel.resolvePageSize(
    typeof window !== 'undefined' && window.innerWidth <= resolvedShowMoreConfig.value.mobileMaxWidthPx
  )
);
const showMoreVisibleCount = ref(0);
const showMoreButtonVisible = ref(false);
let showMoreViewportBroadcaster = null;
let unsubscribeShowMoreViewport = null;

const syncShowMoreViewState = () => {
  showMoreVisibleCount.value = showMoreRevealModel.getVisibleCount();
  showMoreButtonVisible.value = showMoreRevealModel.shouldShowButton();
};

const resetShowMoreForCurrentList = () => {
  showMoreRevealModel.applyOptions(resolvedShowMoreConfig.value);
  const totalCount = filteredEventsForList.value?.length || 0;
  showMoreRevealModel.resetToFirstPage(totalCount, showMoreCurrentPageSize.value);
  syncShowMoreViewState();
};

const visibleEventsForList = computed(() => {
  const eventsList = filteredEventsForList.value || [];
  if (!resolvedShowMoreConfig.value.enabled) {
    return eventsList;
  }
  return eventsList.slice(0, showMoreVisibleCount.value);
});

const handleShowMoreReveal = () => {
  showMoreRevealModel.revealNextPage(showMoreCurrentPageSize.value);
  syncShowMoreViewState();
};

// Функция для ручного выбора события
const showInfo = (item) => {
  selectedEvent.value = item;
  activeEventId.value = getEventId(item); // Используем уникальный ID

  // Также обновляем selectedDate при ручном выборе события
  const eventStartDate = parseDate(item.date_start);
  selectedDate.value = eventStartDate;
}

// Инициализация выбранного месяца при монтировании
onMounted(() => {
  initializeHighlightedDates();

  const currentYear = new Date().getFullYear();
  if (targetYear.value !== currentYear) {
    selectedMonth.value = {
      year: targetYear.value,
      month: 1
    };
  } else {
    const now = new Date();
    selectedMonth.value = {
      year: now.getFullYear(),
      month: now.getMonth() + 1
    };
  }

  showMoreViewportBroadcaster = new ViewportBreakpointBroadcaster(resolvedShowMoreConfig.value);
  unsubscribeShowMoreViewport = showMoreViewportBroadcaster.subscribe((snapshot) => {
    showMoreCurrentPageSize.value = snapshot.pageSize;
    showMoreRevealModel.syncAfterViewportPageSizeChange(snapshot.pageSize);
    syncShowMoreViewState();
  });
  showMoreViewportBroadcaster.start();
  resetShowMoreForCurrentList();
})

onUnmounted(() => {
  if (unsubscribeShowMoreViewport) {
    unsubscribeShowMoreViewport();
    unsubscribeShowMoreViewport = null;
  }
  if (showMoreViewportBroadcaster) {
    showMoreViewportBroadcaster.stop();
    showMoreViewportBroadcaster = null;
  }
})

// Отдельные watchers для разных свойств
watch(() => props.events, () => {
  initializeHighlightedDates();
}, { deep: true });

watch(() => props.year, () => {
  initializeHighlightedDates();
  activeEventId.value = null;
  selectedEvent.value = undefined;

  const currentYear = new Date().getFullYear();
  if (targetYear.value !== currentYear) {
    selectedMonth.value = {
      year: targetYear.value,
      month: 1
    };
  } else {
    const now = new Date();
    selectedMonth.value = {
      year: now.getFullYear(),
      month: now.getMonth() + 1
    };
  }
});

watch(() => props.type, () => {
  initializeHighlightedDates();
  selectedEvent.value = undefined;
  activeEventId.value = null;
});

watch(filteredEventsForList, () => {
  resetShowMoreForCurrentList();
});

watch(resolvedShowMoreConfig, (nextConfig) => {
  showMoreRevealModel.applyOptions(nextConfig);
  if (showMoreViewportBroadcaster) {
    showMoreViewportBroadcaster.stop();
    if (unsubscribeShowMoreViewport) {
      unsubscribeShowMoreViewport();
    }
    showMoreViewportBroadcaster = new ViewportBreakpointBroadcaster(nextConfig);
    unsubscribeShowMoreViewport = showMoreViewportBroadcaster.subscribe((snapshot) => {
      showMoreCurrentPageSize.value = snapshot.pageSize;
      showMoreRevealModel.syncAfterViewportPageSizeChange(snapshot.pageSize);
      syncShowMoreViewState();
    });
    showMoreViewportBroadcaster.start();
  }
  resetShowMoreForCurrentList();
}, { deep: true });
</script>

<template>
  <div class="events-list">
    <div class="events-list__calendar">
      <VueDatePicker
          :model-value="selectedDate"
          :start-date="startDate"
          :min-date="minDate"
          :max-date="maxDate"
          prevent-min-max-navigation
          inline
          auto-apply
          locale="ru"
          month-name-format="long"
          :enable-time-picker="false"
          :month-change-on-scroll="false"
          @update:model-value="handleDateClick"
          @update-month-year="handleMonthChange"
          :highlight="highlightedDates"
          :allowed-dates="highlightedDates"
          :enable-year-picker="false"
          :enable-month-picker="false"
          :hide-offset-dates="true"
      />
    </div>
    <div class="events-list__events">
      <template v-if="filteredEventsForList && filteredEventsForList.length > 0">
        <a
            href="#eventInfo"
            :class="['events-list__item', { 'events-list__item_active': activeEventId === getEventId(event) }]"
            v-for="event in visibleEventsForList"
            :key="getEventId(event)"
            @click="showInfo(event)"
        >
          <div class="events-list__item-date" v-html="formatDateRange(event.date_start, event.date_finish)"></div>
          <h3 class="events-list__item-name">{{ event.name }}</h3>
          <div class="events-list__item-details">
            <span>{{ eventTypeName[event.type] }}</span>
            <template v-if="event.city">
              <span>•</span>
              <span>{{ event.city }}</span>
            </template>
          </div>
        </a>
      </template>
      <div class="events-list__empty" v-else>
        Нет мероприятий
        <span style="text-transform: lowercase">{{ type ? eventTypeName[type] : '' }}</span>
        {{ formatEmptyListMonthPhrase(selectedMonth) }}
      </div>
    </div>
    <div class="events-list__show-more" v-if="showMoreButtonVisible">
      <ShowMoreButton
          :label="resolvedShowMoreConfig.buttonLabel"
          @reveal-more="handleShowMoreReveal"
      />
    </div>
  </div>
  <div id="eventInfo" v-if="selectedEvent || !filteredEventsForList?.length">
    <div
        :class="['event-info', {'event-info_with-logo': selectedEvent?.seminars || selectedEvent?.partners}]"
        v-if="selectedEvent?.description"
    >
      <img :src="require(`@/assets/img/partners-seminars-logo.png`)" alt="Partners seminars" class="event-info__logo" v-if="selectedEvent.seminars"/>
      <img :src="require(`@/assets/img/partners-logo.png`)" alt="Partners" class="event-info__logo" v-if="selectedEvent.partners"/>
      <div class="event-info__header">
        <span class="event-info__date" v-html="formatDateRange(selectedEvent.date_start, selectedEvent.date_finish, false)"></span>
        <span class="event-info__city" v-if="selectedEvent.city">
          / {{ selectedEvent.city}}
        </span>
      </div>
      <div v-html="selectedEvent.description" class="event-info__description"></div>
      <div class="event-info__address" v-if="selectedEvent.address">
        <h3>Место проведения:</h3>
        <p>{{ selectedEvent.address }}</p>
      </div>
      <a :href="selectedEvent.link" class="event-info__button" target="_blank" v-if="selectedEvent.link">Регистрация</a>
    </div>
    <div v-else class="event-placeholder">
      <div class="event-placeholder__image">
        <img :src="require(`@/assets/img/info-placeholder.png`)" alt="">
      </div>
      <p>Следите за<br/>обновлениями</p>
    </div>
  </div>
</template>